/**
 * KAN-702: материалы оператора: список и изменения.
 * KAN-721: материалы для отправки из переписки и ручная отправка клиенту.
 */
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import {
  instructionAttachmentsApi,
  type CreateInstructionAttachmentPayload,
  type UpdateInstructionAttachmentPayload,
} from '@/services/api/instructionAttachments';

const ROOT_KEY = 'instruction-attachments';

/** Без operatorId - ключ списка всех видимых материалов. */
export const instructionAttachmentsQueryKey = (operatorId?: string | null) =>
  [ROOT_KEY, operatorId || 'all'] as const;

/** Текст ошибки: detail с бэкенда, иначе общий текст. */
export function attachmentErrorMessage(error: unknown, fallback: string): string {
  const data = (error as { response?: { data?: { detail?: unknown } } })?.response?.data;
  if (data && typeof data.detail === 'string' && data.detail) return data.detail;
  return fallback;
}

export function useInstructionAttachments(operatorId: string) {
  return useQuery({
    queryKey: instructionAttachmentsQueryKey(operatorId),
    queryFn: () => instructionAttachmentsApi.list(operatorId),
    enabled: !!operatorId,
  });
}

export function useInstructionAttachmentMutations(operatorId: string) {
  const client = useQueryClient();
  // Весь корень: список "все операторы" в переписке тоже должен обновиться.
  const invalidate = () => client.invalidateQueries({ queryKey: [ROOT_KEY] });

  const create = useMutation({
    mutationFn: (payload: Omit<CreateInstructionAttachmentPayload, 'operatorId'>) =>
      instructionAttachmentsApi.create({ ...payload, operatorId }),
    onSuccess: invalidate,
    onError: (error: unknown) =>
      toast.error(attachmentErrorMessage(error, 'Не удалось добавить материал')),
  });

  const update = useMutation({
    mutationFn: ({ id, ...data }: UpdateInstructionAttachmentPayload & { id: number }) =>
      instructionAttachmentsApi.update(id, data),
    onSuccess: invalidate,
    onError: (error: unknown) =>
      toast.error(attachmentErrorMessage(error, 'Не удалось сохранить материал')),
  });

  const remove = useMutation({
    mutationFn: (id: number) => instructionAttachmentsApi.remove(id),
    onSuccess: invalidate,
    onError: (error: unknown) =>
      toast.error(attachmentErrorMessage(error, 'Не удалось удалить материал')),
  });

  return { create, update, remove };
}

/**
 * KAN-721: включённые материалы для отправки из переписки. operatorId null -
 * материалы всех операторов, видимых текущему пользователю.
 */
export function useSendableMaterials(operatorId: string | null, enabled: boolean) {
  return useQuery({
    queryKey: instructionAttachmentsQueryKey(operatorId),
    queryFn: () => instructionAttachmentsApi.list(operatorId),
    enabled,
    select: items => items.filter(item => item.enabled),
  });
}

/** KAN-721: отправить материал клиенту, toast об успехе или с detail ошибки. */
export function useSendMaterial(clientId: string) {
  return useMutation({
    mutationFn: (id: number) => instructionAttachmentsApi.send(id, clientId),
    onSuccess: () => {
      toast.success('Отправлено');
    },
    onError: (error: unknown) =>
      toast.error(attachmentErrorMessage(error, 'Не удалось отправить материал')),
  });
}
